import { useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { AUDIT_LOG_KEYS } from './audit-logs'
import { api } from './client'
import type { paths } from './schema'

// Adjustments (UI-SCREENS I-10 / I-11 / I-12, #67): a stock count with a reason, posted as one
// movement per stock item. Owner and Staff both record and read them, at the materials, WIP
// (04l #140), Home and Sales Channel (05e #173) locations.
export type Adjustment =
  paths['/api/w/{slug}/adjustments']['get']['responses'][200]['content']['application/json'][number]
export type AdjustmentLine = Adjustment['lines'][number]
export type AdjustmentMovement = Adjustment['movements'][number]
export type AdjustmentBody = NonNullable<
  paths['/api/w/{slug}/adjustments']['post']['requestBody']
>['content']['application/json']

// A WIP item the form picks from at the WIP location: Model, Product, colors, pre-vN flag and
// current qty (04l #140).
export type WipItem =
  paths['/api/w/{slug}/wip_items']['get']['responses'][200]['content']['application/json'][number]

export const LOCATIONS = ['materials', 'wip', 'home', 'channel'] as const
export type AdjustmentLocation = (typeof LOCATIONS)[number]

// The reasons a count may carry per location; `unused_part` is a WIP write-off and the API refuses
// it anywhere else.
export const MATERIALS_REASONS = ['count', 'damaged', 'lost', 'write_off'] as const
export const WIP_REASONS = [...MATERIALS_REASONS, 'unused_part'] as const
export type AdjustmentReason = (typeof WIP_REASONS)[number]

export const reasonsFor = (location: AdjustmentLocation): readonly string[] =>
  location === 'wip' ? WIP_REASONS : MATERIALS_REASONS

// How a document names its location on I-10 and I-12: the location kind, and for a channel
// document its Sales Channel (05e #173).
export function locationLabel(t: TFunction, location: Adjustment['location']): string {
  const kind = t(`adjustments.locations.${location.kind}`)
  return location.sales_channel ? `${kind} · ${location.sales_channel.name}` : kind
}

// "DRG-BLACK · Black": how a Variant is named on an Adjustment line or movement at Home or a
// channel.
export const variantLabel = (variant: { sku: string; name: string }) => `${variant.sku} · ${variant.name}`

// What a document's lines are counted in on I-10: Pools, WIP parts or Variants.
export const lineCountKey = (kind: string) =>
  kind === 'wip'
    ? 'adjustments.lineCountWip'
    : kind === 'materials'
      ? 'adjustments.lineCount'
      : 'adjustments.lineCountVariant'

const LIST = ['get', '/api/w/{slug}/adjustments'] as const
const ADJUSTMENT = ['get', '/api/w/{slug}/adjustments/{id}'] as const

// Only a plain count needs no explanation (02 Materials).
export const reasonNeedsNote = (reason: string) => reason !== 'count'

export function useAdjustments(slug: string) {
  return api.useQuery('get', '/api/w/{slug}/adjustments', { params: { path: { slug } } }, { retry: false })
}

export function useWipItems(slug: string, { enabled = true }: { enabled?: boolean } = {}) {
  return api.useQuery('get', '/api/w/{slug}/wip_items', { params: { path: { slug } } }, { retry: false, enabled })
}

// "Bracket · Body — Warna utama: Bambu · PLA · Basic · Black": the Product and Model, then the Pool
// printed in each color slot.
export function wipItemLabel(item: Pick<WipItem, 'model' | 'colors'>): string {
  const colors = item.colors.map((color) => `${color.label}: ${color.pool.label}`).join(', ')
  const name = `${item.model.product.name} · ${item.model.name}`
  return colors === '' ? name : `${name} — ${colors}`
}

export function useAdjustment(slug: string, id: number) {
  return api.useQuery('get', '/api/w/{slug}/adjustments/{id}', { params: { path: { slug, id } } }, { retry: false })
}

export function useCreateAdjustment() {
  const onSettled = useRefetch()
  return api.useMutation('post', '/api/w/{slug}/adjustments', { onSettled })
}

// Voiding one posts the exact reversal of the count (#68), so the same queries change as when
// it was recorded.
export function useVoidAdjustment() {
  const onSettled = useRefetch()
  return api.useMutation('post', '/api/w/{slug}/adjustments/{adjustment_id}/void', { onSettled })
}

// The note is the only editable field of a posted record (domain-rules Corrections); no stock
// moves, so only the document and the list are refetched.
export function useUpdateAdjustmentNote() {
  const queryClient = useQueryClient()
  return api.useMutation('patch', '/api/w/{slug}/adjustments/{id}', {
    onSettled: async () => {
      await Promise.all(
        [LIST, ADJUSTMENT, AUDIT_LOG_KEYS.adjustments].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey: [...queryKey] }),
        ),
      )
    },
  })
}

// Anything that writes stock movements changes the Pool list, a Pool and its movement history
// too; at the WIP location, the WIP items and Buildable; at Home or a channel, the Stock view and
// the movement history (05e #173).
function useRefetch() {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all(
      [
        LIST,
        ADJUSTMENT,
        AUDIT_LOG_KEYS.adjustments,
        ['get', '/api/w/{slug}/pools'],
        ['get', '/api/w/{slug}/pools/{id}'],
        ['get', '/api/w/{slug}/pools/{pool_id}/movements'],
        ['get', '/api/w/{slug}/wip_items'],
        ['get', '/api/w/{slug}/variants/{variant_id}/buildable'],
        ['get', '/api/w/{slug}/stock'],
        ['get', '/api/w/{slug}/movements'],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
    )
  }
}
